import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  IconButton,
  Chip,
} from "@mui/material";
import styles from "./ArticleReaderCard.module.scss";
import { ArrowIcon } from "../../components/icon-components/ArrowIcon";
import PlayIcon from "../../components/icon-components/PlayIcon";
import CustomButton from "../../components/v2/custom-button/CustomButton";
import CompleteIllustratorIcon from "../../components/icon-components/CompleteIllustratorIcon";
import FallbackImage from "../../assets/icons/learning_no_image.svg";
import DOMPurify from "dompurify";
import { useTranslation } from "react-i18next";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import CheckedCircleIcon from "../../components/icon-components/CheckedCircleIcon";

interface ArticleReaderCardProps {
  category?: string;
  isNew?: boolean;
  fullHtml?: string;
  title?: string;
  isCompleteBtnVisible?: boolean;
  activityPoints?: number;
  audioUrl?: string;
  thumbnail?: string;
  publishedDate?: string;
  publisherName?: string;
  hideCheckMark?: boolean;
  onComplete: () => void;
  navigateBack: () => void;
}

const ArticleReaderCard: React.FC<ArticleReaderCardProps> = ({
  category,
  isNew,
  fullHtml,
  title,
  isCompleteBtnVisible,
  activityPoints,
  publishedDate,
  publisherName,
  audioUrl,
  thumbnail,
  hideCheckMark,
  onComplete,
  navigateBack,
}) => {
  const { t } = useTranslation();
  const [imageError, setImageError] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);

  const sanitizedContent = DOMPurify.sanitize(fullHtml || "");

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, []);

  const toggleAudio = () => {
    if (!audioRef.current && audioUrl) {
      audioRef.current = new Audio(audioUrl);
      audioRef.current.addEventListener("loadedmetadata", () => {
        setDuration(audioRef.current!.duration);
      });
      audioRef.current.addEventListener("ended", () => {
        setIsPlaying(false);
      });
    }

    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audio.preload = "metadata";

      const onLoadedMetadata = () => {
        setDuration(audio.duration);
      };

      audio.addEventListener("loadedmetadata", onLoadedMetadata);
      // Start loading the metadata
      audio.load();

      return () => {
        audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      };
    }
  }, [audioUrl]);

  const formatTime = (seconds: number): number => {
    if (!seconds || isNaN(seconds)) return 0;
    const minutes = seconds / 60;
    return seconds < 60 ? parseFloat(minutes.toFixed(2)) : Math.floor(minutes);
  };

  return (
    <Box className={styles.container}>
      {/* Header */}
      <Box className={styles.headingContainer}>
        <Box className={styles.leftHeader}>
          <IconButton onClick={navigateBack} sx={{ paddingLeft: 0 }}>
            <ArrowIcon width={20} height={15} />
          </IconButton>
          <Typography variant="bodyOverline" className={styles.categoryText}>
            {category}
          </Typography>
        </Box>
        <Box className={styles.rightHeader}>
          {!isCompleteBtnVisible && !hideCheckMark && (
            <CheckedCircleIcon width={20} height={20} variant={"completed"} />
          )}
          {isNew && (
            <Chip
              label={t("educationContent.new")}
              className={styles.newChip}
            />
          )}
          {activityPoints! > 0 && (
            <Chip
              label={t("viewLearningContent.points", {
                points: activityPoints,
              })}
              className={styles.pointsChip}
            />
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box className={styles.contentContainer}>
        <Typography variant="h2">{title}</Typography>
        {publisherName && (
          <Box>
            <Typography variant="body2">{publisherName}</Typography>
            <Typography variant="body2" sx={{ color: "#666666" }}>
              {publishedDate}
            </Typography>
          </Box>
        )}

        <Box className={styles.articleImageWrapper}>
          <img
            loading="lazy"
            src={imageError ? FallbackImage : thumbnail || ""}
            alt="Article visual"
            onError={() => setImageError(true)}
            className={styles.articleImage}
            style={
              imageError
                ? { width: "46px", height: "46px", objectFit: "contain" }
                : { width: "100%", height: "100%", objectFit: "cover" } // take full space
            }
          />
        </Box>

        {audioUrl && (
          <Box className={styles.audioContainer}>
            <Box display="flex" alignItems="center" gap={2}>
              <IconButton onClick={toggleAudio}>
                {isPlaying ? (
                  <PauseCircleFilledIcon sx={{ color: "#BCD0BF" }} />
                ) : (
                  <PlayIcon />
                )}
              </IconButton>
              <Typography variant="body1">
                {t("viewLearningContent.listenToArticle")}
              </Typography>
            </Box>
            {duration !== null && (
              <Typography variant="bodySmall" className={styles.audioDuration}>
                {formatTime(duration) < 1
                  ? t("educationContent.lessThanAMin")
                  : t("viewLearningContent.mediaDurationInminutes", {
                      minutes: formatTime(duration),
                    })}
              </Typography>
            )}
          </Box>
        )}

        <Box className={styles.articleScrollWrapper}>
          <Box
            dangerouslySetInnerHTML={{ __html: sanitizedContent }}
            className={styles.modalContent}
          />
        </Box>

        {/* Completion Card */}
        {isCompleteBtnVisible && (
          <Card className={styles.completeCard}>
            <CardContent>
              <Box className={styles.completeContainer}>
                <CompleteIllustratorIcon />
                <Box className={styles.confirmMessage}>
                  <Typography variant="h5">
                    {t("viewLearningContent.readArticleMessage")}
                  </Typography>
                  <Typography variant="body1">
                    {t("viewLearningContent.completeConfirmationMessage", {
                      activityPoints: activityPoints,
                    })}
                  </Typography>
                  <CustomButton
                    variant="contained"
                    color="primary"
                    text={t("completedBtn")}
                    sx={{ width: "134px" }}
                    onClick={onComplete}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        )}
      </Box>
    </Box>
  );
};

export default ArticleReaderCard;
